/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Formik, yupToFormErrors, Field as FormikField } from 'formik'
import { FormGroup, Button } from 'react-bootstrap'
import moment from 'moment'
import 'moment-timezone'
import DatePicker from '~/components/DatePicker/CustomDatePicker'
import ValidationSchema from './FormValidation'
import { useServiceContext } from '../../root'
import ErrorMessage from '../ErrorMessage'
import { Select } from '../Select'
import Field from '../shared/Field'
import Loader from '../shared/Loader'
import Utils from '../../lib/Utils'
import { getDatePickerFormat } from '~/utils/dateFormatting'

// Define ControlLabel component locally (not in react-bootstrap)
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`control-label ${className || ''}`}>{children}</label>
)

const REPEAT_MODE = {
  monthly: 'monthly',
  weekly: 'weekly',
  daily: 'daily'
} as const

const AM_PM = {
  am: 'am',
  pm: 'pm'
} as const

const ENDS = {
  yes: 'yes',
  no: 'no'
} as const

interface SaveAndShareProps {
  onSubmit: (name: string, schedule: any, sharedWith: any) => Promise<any>
  doClose: () => void
  type: string
  isEdit?: boolean
  item?: any
  isHideScheduleSettings?: boolean
}

interface SelectOption {
  value: any
  label: string
}

interface FormValues {
  name: string
  sharingWith: string[]
  schedulingEnabled: boolean
  scheduleStartAt: Date | null
  timeZoneId: string
  repeatAtHour: number
  period: string
  repeatMode: string
  repeatOnDayInMonth: number
  repeatOnTheLastDayOfTheMonth: boolean
  scheduleEndAt: Date | null
  scheduling_day: string[]
  ends: string
}

const SaveAndShare: React.FC<SaveAndShareProps> = ({
  onSubmit,
  doClose,
  type,
  isEdit = false,
  item,
  isHideScheduleSettings = false
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()

  const repeatModeOptions: SelectOption[] = [
    { label: translate('SAVED_FILTERS_SCHEDULING_REPEAT_MODE_DAILY'), value: REPEAT_MODE.daily },
    { label: translate('SAVED_FILTERS_SCHEDULING_REPEAT_MODE_WEEKLY'), value: REPEAT_MODE.weekly },
    { label: translate('SAVED_FILTERS_SCHEDULING_REPEAT_MODE_MONTHLY'), value: REPEAT_MODE.monthly }
  ]

  const hours: SelectOption[] = []
  for (let i = 1; i <= 12; i++) {
    hours.push({ label: String(i), value: i })
  }

  const period: SelectOption[] = [
    { value: AM_PM.am, label: 'AM' },
    { value: AM_PM.pm, label: 'PM' }
  ]

  const arrayOfDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  const dayOfMonthOptions: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({
    label: `Day ${i + 1} of the month`,
    value: i + 1
  }))

  const defaultFilterSchedule: FormValues = {
    name: '',
    sharingWith: ['USER-' + context.service.auth.getUserId()],
    schedulingEnabled: !(isEdit && item),
    scheduleStartAt: moment().toDate(),
    timeZoneId: (moment as any).tz?.guess() || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Calcutta',
    repeatAtHour: 1,
    period: AM_PM.pm,
    repeatMode: REPEAT_MODE.daily,
    repeatOnDayInMonth: 1,
    repeatOnTheLastDayOfTheMonth: false,
    scheduleEndAt: null,
    scheduling_day: ['1'],
    ends: ENDS.no
  }

  const [state, setState] = useState({
    loading: true,
    formValues: defaultFilterSchedule,
    allUsers: [] as SelectOption[],
    timezones: [] as SelectOption[],
    todayDate: moment().toDate()
  })

  const assembleFromServer = (item: any): FormValues => {
    const formValues: FormValues = {
      ...defaultFilterSchedule,
      name: item.name,
      sharingWith: [
        ...(item.filterSharing?.shareUserIds?.map((i: number) => 'USER-' + i) || []),
        ...(item.filterSharing?.shareGroupIds?.map((i: number) => 'GROUP-' + i) || [])
      ],
      schedulingEnabled: false
    }

    if (item.filterSchedule?.schedulingEnabled) {
      Object.assign(formValues, {
        schedulingEnabled: item.filterSchedule.schedulingEnabled,
        repeatAtHour: 1,
        period: AM_PM.pm,
        repeatMode: (function (schedule: any) {
          if (typeof schedule.repeatDaily === 'number') {
            return REPEAT_MODE.daily
          } else if (Array.isArray(schedule.repeatWeeklyOnDayInWeek)) {
            return REPEAT_MODE.weekly
          } else if (typeof schedule.repeatMontly === 'number') {
            return REPEAT_MODE.monthly
          }
          return REPEAT_MODE.daily
        })(item.filterSchedule),
        repeatOnDayInMonth: item.filterSchedule.repeatOnDayInMonth,
        repeatOnTheLastDayOfTheMonth: item.filterSchedule.repeatOnTheLastDayOfTheMonth,
        scheduleStartAt: null,
        scheduleEndAt: null,
        ends: ENDS.no,
        timeZoneId: item.filterSchedule.timeZoneId,
        scheduling_day: item.filterSchedule.repeatWeeklyOnDayInWeek
          ? item.filterSchedule.repeatWeeklyOnDayInWeek.map((i: number) => i.toString())
          : []
      })

      if (item.filterSchedule.scheduleStartAt) {
        formValues.scheduleStartAt = moment(item.filterSchedule.scheduleStartAt).toDate()
      }
      if (item.filterSchedule.scheduleEndAt) {
        formValues.scheduleEndAt = moment(item.filterSchedule.scheduleEndAt).toDate()
        formValues.ends = ENDS.yes
      }
      if (typeof item.filterSchedule.repeatAtHour === 'number') {
        const hour = fixTimeOverflow(item.filterSchedule.repeatAtHour)
        const isAm = getIsHourAm(hour)
        formValues.repeatAtHour = transformSchedulingIntoSelectedTime(hour, !isAm)
        formValues.period = isAm ? AM_PM.am : AM_PM.pm
      }
    }

    return formValues
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    const promises: Promise<any>[] = []

    if (isEdit && item) {
      promises.push(
        context.api.formFilters.loadFromServer(item.id, item.filterType).then(async (data: any) => {
          setState((prev) => ({ ...prev, formValues: assembleFromServer(data) }))
        })
      )
    }

    promises.push(
      context.api.users.getAssembledUsersGroups(true).then((data: any) => {
        setState((prev) => ({
          ...prev,
          allUsers: Utils.transformToSelectOptions(data.merged, '_uid', 'name', 'id')
        }))
      })
    )

    promises.push(
      context.api.settings
        .getTimezones()
        .then((data: any) => {
          const transformedTimezones = Utils.transformToSelectOptions(data, 'zoneId', 'displayValue', 'zoneId')
          setState((prev) => ({
            ...prev,
            timezones: transformedTimezones
          }))
        })
        .catch((error) => {
          console.error('Error loading timezones:', error)
        })
    )

    if (promises.length) {
      Promise.allSettled(promises).then(() => {
        setState((prev) => ({ ...prev, loading: false }))
      })
    }
  }

  const getIsHourAm = (hour: number): boolean => {
    return (hour >= 0 && hour < 12) || hour === 24
  }

  const transformSchedulingIntoSelectedTime = (hour: number, isPm: boolean): number => {
    if (hour === 0) {
      return 12
    } else if (isPm && hour > 12) {
      return hour - 12
    }
    return hour
  }

  const transformSelectedIntoSchedulingTime = (hour: number, isPm: boolean): number => {
    const isAm = !isPm
    let adjustedHour = hour

    if (isAm && hour === 12) {
      adjustedHour = 0
    } else if (isPm && hour !== 12) {
      adjustedHour += 12
    }

    return adjustedHour
  }

  const fixTimeOverflow = (hour: number): number => {
    if (hour < 0) {
      return hour + 24
    } else if (hour >= 24) {
      return hour - 24
    }
    return hour
  }

  const save = (values: FormValues) => {
    setState((prev) => ({ ...prev, loading: true }))

    const filterSchedule: any = {
      ...values,
      repeatDaily: null,
      repeatDailyStartOnDayInWeek: null,
      repeatMontly: null,
      repeatOnDayInMonth: null,
      repeatWeeklyOnDayInWeek: null
    }

    // Remove fields that shouldn't be in the schedule
    delete filterSchedule.name
    delete filterSchedule.scheduling_day
    delete filterSchedule.ends
    delete filterSchedule.sharingWith
    delete filterSchedule.period
    delete filterSchedule.repeatMode

    if (values.repeatMode === REPEAT_MODE.daily) {
      filterSchedule.repeatDaily = 1
      filterSchedule.repeatDailyStartOnDayInWeek = 1
    } else if (values.repeatMode === REPEAT_MODE.weekly) {
      filterSchedule.repeatWeeklyOnDayInWeek = values.scheduling_day.map((i) => parseInt(i)) || []
    } else if (values.repeatMode === REPEAT_MODE.monthly) {
      filterSchedule.repeatMontly = 1
      filterSchedule.repeatOnDayInMonth = parseInt(String(values.repeatOnDayInMonth))
    }

    filterSchedule.repeatAtHour = fixTimeOverflow(
      transformSelectedIntoSchedulingTime(values.repeatAtHour, values.period === AM_PM.pm)
    )

    if (filterSchedule.scheduleStartAt && filterSchedule.scheduleStartAt.utc) {
      const utcDateScheduleStartAt = filterSchedule.scheduleStartAt.utc(true)
      utcDateScheduleStartAt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      filterSchedule.scheduleStartAt = utcDateScheduleStartAt.valueOf()
    }

    if (filterSchedule.scheduleEndAt && filterSchedule.scheduleEndAt.utc) {
      const utcDateScheduleEndAt = filterSchedule.scheduleEndAt.utc(true)
      utcDateScheduleEndAt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      filterSchedule.scheduleEndAt = utcDateScheduleEndAt.valueOf()
    }

    const shareList = values.sharingWith.map((i) => {
      const userGroupInfoArr = i.split('-')
      return { id: parseInt(userGroupInfoArr[1]), systemHelperType: userGroupInfoArr[0] }
    })

    onSubmit(values.name, filterSchedule, shareList).catch(() => {
      setState((prev) => ({ ...prev, loading: false }))
    })
  }

  return (
    <Loader blocking={state.loading}>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => ({}),
              (err: any) => yupToFormErrors(err)
            )
        }}
        enableReinitialize={true}
        onSubmit={(values) => {
          save(values)
        }}
        initialValues={state.formValues}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <>
            <div className="modal-body">
              <form>
                <FormGroup controlId="save-report-name">
                  <ControlLabel>{translate('METADATA_FORM_LABEL_NAME')}</ControlLabel>
                  <div className="row">
                    <div className="col-xs-6">
                      <FormikField
                        className="form-control field-control"
                        type="text"
                        name="name"
                        maxLength={255}
                        autoFocus
                        id="save-report-name-input"
                      />
                    </div>
                  </div>
                  <ErrorMessage name="name" />
                </FormGroup>

                <FormGroup className="form-group md-form-group share-with-group" controlId="save-report-sharing">
                  <ControlLabel>{translate('SAVED_FILTERS_SAVE_SHARE')}</ControlLabel>
                  <div className="row">
                    <div className="col-xs-6">
                      <Select
                        placeholder={translate('SELECT_USER(S)...')}
                        name="sharingWith"
                        value={values.sharingWith}
                        options={state.allUsers}
                        onChange={(option: any) => {
                          setFieldValue('sharingWith', option ? option.map((o: any) => o.value) : [])
                        }}
                        multi={true}
                        showNewOptionAtTop={true}
                        searchable
                        id="save-report-sharing-select"
                      />
                    </div>
                  </div>
                  <ErrorMessage name="sharingWith" />
                </FormGroup>

                {!isHideScheduleSettings && (
                  <>
                    <FormGroup controlId="save-report-scheduling-enabled">
                      <FormikField name="schedulingEnabled">
                        {({ field }: any) => (
                          <label>
                            <input
                              type="checkbox"
                              {...field}
                              checked={values.schedulingEnabled}
                              id="save-report-scheduling-checkbox"
                            />{' '}
                            {translate('SAVED_FILTERS_SCHEDULING_ENABLED')}
                          </label>
                        )}
                      </FormikField>
                    </FormGroup>

                    {values.schedulingEnabled && (
                      <>
                        <div className="row no-gutters">
                          <div className="col-xs-6">
                            <div className="row no-gutters">
                              <div className="col-xs-11 col-xs-push-1 pl15 ml10">
                                <div className="mb10">
                                  <FormGroup controlId="save-report-schedule-start">
                                    <ControlLabel>{translate('SAVED_FILTERS_SCHEDULING_REPEAT_START')}</ControlLabel>
                                    <div className="row">
                                      <div className="col-xs-6 form-group md-form-group mb0">
                                        <DatePicker
                                          name="scheduleStartAt"
                                          selected={values.scheduleStartAt}
                                          onChange={(date: Date | null) => {
                                            setFieldValue('scheduleStartAt', date)
                                          }}
                                          popoverTargetOffset="3px 0"
                                          todayButton="Today"
                                          minDate={state.todayDate}
                                          maxDate={values.scheduleEndAt || undefined}
                                          dateFormatCalendar="MMMM"
                                          showYearDropdown
                                          className="form-control field-control w100p"
                                          id="save-report-schedule-start-date"
                                        />
                                      </div>
                                    </div>
                                  </FormGroup>
                                </div>

                                <div className="mb10">
                                  <FormGroup controlId="save-report-repeat-mode">
                                    <ControlLabel className="d-block">
                                      {translate('SAVED_FILTERS_SCHEDULING_REPEAT_EVERY')}
                                    </ControlLabel>
                                    <div className="row">
                                      <div className="col-xs-5">
                                        <Select
                                          isClearable={false}
                                          name="repeatMode"
                                          value={values.repeatMode}
                                          options={repeatModeOptions}
                                          onChange={(option: any) => {
                                            setFieldValue('repeatMode', option.value)
                                          }}
                                          multi={false}
                                          showNewOptionAtTop={true}
                                          searchable
                                          id="save-report-repeat-mode-select"
                                        />
                                      </div>
                                    </div>
                                    <ErrorMessage name="repeatMode" />
                                  </FormGroup>
                                </div>

                                <div className="mb10">
                                  <FormGroup controlId="save-report-repeat-at">
                                    <ControlLabel className="d-block">
                                      {translate('SAVED_FILTERS_SCHEDULING_REPEAT_AT')}
                                    </ControlLabel>
                                    {values.repeatMode === REPEAT_MODE.weekly && (
                                      <div className="mb10">
                                        {arrayOfDays.map((week, i) => {
                                          const index = i + 1
                                          return (
                                            <div
                                              id={String(index)}
                                              key={index}
                                              className={
                                                'styled-checkbox-button mr5 ' +
                                                (values.scheduling_day.indexOf(index.toString()) > -1 ? 'active' : '')
                                              }
                                            >
                                              <label htmlFor={'day_of_week_' + index}>{week}</label>
                                              <FormikField
                                                type="checkbox"
                                                name="scheduling_day"
                                                value={index}
                                                className="d-inline-block mr10"
                                                id={'day_of_week_' + index}
                                              />
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}

                                    {values.repeatMode === REPEAT_MODE.monthly && (
                                      <div className="mb10">
                                        <FormGroup controlId="save-report-day-of-month">
                                          <Select
                                            name="repeatOnDayInMonth"
                                            value={values.repeatOnDayInMonth}
                                            options={dayOfMonthOptions}
                                            onChange={(option: any) => {
                                              const val = option ? option.value : null
                                              setFieldValue('repeatOnDayInMonth', val)

                                              if (!val || val <= 28) {
                                                setFieldValue('repeatOnTheLastDayOfTheMonth', false)
                                              }
                                            }}
                                            multi={false}
                                            isClearable={false}
                                            showNewOptionAtTop={true}
                                            searchable
                                            id="save-report-day-of-month-select"
                                          />
                                          <ErrorMessage name="repeatOnDayInMonth" />
                                        </FormGroup>
                                        {values.repeatOnDayInMonth > 28 && (
                                          <FormGroup controlId="save-report-last-day" className="w100p">
                                            <FormikField name="repeatOnTheLastDayOfTheMonth">
                                              {({ field }: any) => (
                                                <label>
                                                  <input
                                                    type="checkbox"
                                                    {...field}
                                                    checked={values.repeatOnTheLastDayOfTheMonth}
                                                    id="save-report-last-day-checkbox"
                                                  />{' '}
                                                  {translate('SAVED_FILTERS_SCHEDULING_REPEAT_AT_LAST_DAY')}
                                                </label>
                                              )}
                                            </FormikField>
                                          </FormGroup>
                                        )}
                                      </div>
                                    )}

                                    <div className="row">
                                      <div className="col-xs-8">
                                        <div className="row">
                                          <div className="col-xs-6">
                                            <Select
                                              isClearable={false}
                                              name="repeatAtHour"
                                              value={values.repeatAtHour}
                                              options={hours}
                                              onChange={(option: any) => {
                                                setFieldValue('repeatAtHour', option.value)
                                              }}
                                              multi={false}
                                              showNewOptionAtTop={true}
                                              searchable
                                              id="save-report-hour-select"
                                            />
                                          </div>
                                          <div className="col-xs-6">
                                            <Select
                                              name="period"
                                              value={values.period}
                                              options={period}
                                              onChange={(option: any) => {
                                                setFieldValue('period', option.value)
                                              }}
                                              multi={false}
                                              showNewOptionAtTop={true}
                                              searchable
                                              isClearable={false}
                                              id="save-report-period-select"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </FormGroup>
                                </div>

                                <div className="mb10">
                                  <FormGroup controlId="save-report-timezone">
                                    <Select
                                      className="w100p"
                                      isClearable={false}
                                      name="timeZoneId"
                                      value={values.timeZoneId}
                                      options={state.timezones}
                                      onChange={(option: any) => {
                                        setFieldValue('timeZoneId', option ? option.value : null)
                                      }}
                                      multi={false}
                                      showNewOptionAtTop={true}
                                      searchable
                                      id="save-report-timezone-select"
                                    />
                                    <ErrorMessage name="timeZoneId" />
                                  </FormGroup>
                                </div>

                                <ControlLabel>{translate('SAVED_FILTERS_SCHEDULING_REPEAT_END')}</ControlLabel>
                                <div className="mb10">
                                  <FormikField name="ends">
                                    {({ field }: any) => (
                                      <div className="radio-label-wrapper">
                                        <input
                                          {...field}
                                          type="radio"
                                          value={ENDS.no}
                                          checked={values.ends === ENDS.no}
                                          onChange={(event: any) => {
                                            setFieldValue('scheduleEndAt', null)
                                            setFieldValue('ends', event.target.value)
                                          }}
                                          id="save-report-ends-never"
                                        />
                                        <label htmlFor="save-report-ends-never">
                                          {translate('SAVED_FILTERS_SCHEDULING_REPEAT_ENDS_NEVER')}
                                        </label>
                                      </div>
                                    )}
                                  </FormikField>
                                </div>

                                <div className="row">
                                  <div className="col-xs-2">
                                    <FormikField name="ends">
                                      {({ field }: any) => (
                                        <div className="radio-label-wrapper">
                                          <input
                                            {...field}
                                            type="radio"
                                            value={ENDS.yes}
                                            checked={values.ends === ENDS.yes}
                                            onChange={(event: any) => {
                                              setFieldValue('ends', event.target.value)

                                              const newDate = values.scheduleStartAt
                                                ? moment(values.scheduleStartAt).add(7, 'd').toDate()
                                                : moment().add(7, 'd').toDate()
                                              setFieldValue('scheduleEndAt', newDate)
                                            }}
                                            id="save-report-ends-on"
                                          />
                                          <label htmlFor="save-report-ends-on">{translate('OPTION_ON')}</label>
                                        </div>
                                      )}
                                    </FormikField>
                                  </div>
                                  <div className="col-xs-9 ends-date-picker">
                                    <DatePicker
                                      disabled={values.ends !== ENDS.yes}
                                      name="scheduleEndAt"
                                      selected={values.scheduleEndAt}
                                      onChange={(date: Date | null) => {
                                        setFieldValue('scheduleEndAt', date)
                                      }}
                                      minDate={values.scheduleStartAt || undefined}
                                      popoverTargetOffset="3px 0"
                                      todayButton="Today"
                                      dateFormatCalendar="MMMM"
                                      showYearDropdown
                                      className="form-control field-control w100p"
                                      id="save-report-schedule-end-date"
                                      popperPlacement="top-start"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </form>
            </div>

            <div className="modal-footer">
              <Button data-action={'cancel'} variant="link" onClick={doClose} id="save-report-cancel-btn">
                {translate('CANCEL')}
              </Button>
              <Button
                data-action={'save'}
                className="btn btn-primary text-capitalize"
                onClick={() => handleSubmit()}
                id="save-report-save-btn"
              >
                {translate('SAVE')}
              </Button>
            </div>
          </>
        )}
      </Formik>
    </Loader>
  )
}

export default SaveAndShare
